import {
  Avatar,
  Card,
  ECardBorderRadiusSize,
  ECardMediaSize,
  ELoaderSize,
  ETagVariant,
  Loader,
  Tag,
  Text,
  useIsWindowWidthGreaterThan,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useRouter } from '@/hooks/useRouter';
import { IFullUserInfo } from '@/services/types/user/user';
import { getUserInitials } from '@/utility/getUserInitials';

import { ProfilePhotoMenuButton } from './ProfilePhotoMenuButton/ProfilePhotoMenuButton';

interface IProfilePhotoProps {
  userData: IFullUserInfo;
  isLoading: boolean;
  refetchUser: () => void;
}

export const ProfilePhoto = ({ userData, isLoading, refetchUser }: IProfilePhotoProps) => {
  const intl = useIntl();
  const router = useRouter();
  const desktopView = useIsWindowWidthGreaterThan('sm', true);

  const [showProfilePhotoPopover, setShowProfilePhotoPopover] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  return (
    <div
      onMouseEnter={() => !isLoading && !isUploading && setShowProfilePhotoPopover(true)}
      onMouseLeave={() => setShowProfilePhotoPopover(false)}
      className={`${isLoading || isUploading ? 'pointer-events-none' : ''} relative origin-top-left`}
      aria-label={intl.formatMessage({
        defaultMessage: 'Upload profile photo area',
        id: 'MaX9l0',
      })}>
      <Card
        withBoxShadow={false}
        showHoverEffect={false}
        borderRadiusSize={desktopView ? ECardBorderRadiusSize.Large : ECardBorderRadiusSize.Small}>
        <Card.Media
          image={userData.profile.avatar_url}
          fullRadius
          size={ECardMediaSize.Full}
          className="mb-5 aspect-[1/1]">
          <Tag
            label={intl.formatMessage({
              defaultMessage: 'PROFILE PHOTO',
              id: '7k3/KC',
            })}
            variant={ETagVariant.White}
            className="absolute top-0 left-0 flex m-4"
          />
          {userData.profile && !userData.profile.avatar_url && (
            <Avatar
              className="!h-full !w-full !text-[6rem] md:!text-[8.75rem]"
              initials={getUserInitials(userData.profile.first_name, userData.profile.last_name)}
            />
          )}
        </Card.Media>
      </Card>
      {router.pathname.includes('/edit') && (
        <div className="my-6">
          <Text className=" text-secondary-base md:mb-0">
            <FormattedMessage
              defaultMessage="Upload a photo clearly showing your face to help hosts and guests get to know you."
              id="3zpMk/"
            />
          </Text>
        </div>
      )}
      {isUploading && (
        <div>
          <div className="absolute top-0 w-full h-full bg-white opacity-60" />
          <Loader
            className="absolute top-0 bottom-0 left-0 right-0 m-auto"
            size={ELoaderSize.Medium}
          />
        </div>
      )}
      <ProfilePhotoMenuButton
        userData={userData}
        showPopover={showProfilePhotoPopover}
        loading={isLoading}
        setIsUploading={setIsUploading}
        refetchUser={refetchUser}
      />
    </div>
  );
};
